import { useCallback, useEffect, useState } from 'react';
import {
  createArchivalUnitHierarchyPath,
  createAidHierarchyPath,
  createNestedEadHierarchyPath,
} from './grid-crumbs-hook';

import {
  ArchivalAidMetadata,
  ArchivalCollection,
  ArchivalUnitCopy,
  EadNode,
} from 'types';
import { useHistory, useParams } from 'react-router-dom';
import { getNodeId, singleFetch } from './ctx-utils';
import { getCacheKey } from './grid-ctx-enums';
import { useVariantContext } from './VariantCtx';
import { ApiRoute } from 'enums';
import { useEadTreeContext } from './EadTreeCtx';

const aidPrefix = '/api/archival-aid-metadata';
const collectionPrefix = '/api/archival-collection';
const archivalUnitApi = '/api/' + ApiRoute.ARCHIVAL_UNIT;

const useProcessCollection = (
  id: string,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const etctx = useEadTreeContext();
  const clearContext = useClearContext();
  const { push } = useHistory();
  const { setCrumbs, setCurrentArchivalCollection } = etctx;

  const processCollectionDetail = useCallback(async () => {
    setLoading(true);
    clearContext();
    const ac = await singleFetch<ArchivalCollection>(
      `${collectionPrefix}/${id}`,
      `collection-detail-${id}`
    );
    if (!ac) {
      setLoading(false);
      clearContext();
      push('/not-found');

      return;
    }
    setCurrentArchivalCollection?.(ac);
    setCrumbs?.([
      {
        id: ac.id,
        index: 0,
        label: ac.name ?? '',
        to: `/fonds/0/${ac.id}`,
        level: 'fonds',
      },
    ]);
    setLoading(false);
  }, [
    clearContext,
    id,
    setCrumbs,
    setCurrentArchivalCollection,
    setLoading,
    push,
  ]);

  return { processCollectionDetail };
};

const useProcessAidChildren = (
  id: string,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const etctx = useEadTreeContext();
  const variant = useVariantContext();
  const clearContext = useClearContext();
  const { push } = useHistory();
  const {
    setCrumbs,
    setCurrentArchivalUnit,
    setCurrentArchivalAid,
    setNextIdsToExpand,
    setCurrentEadNode,
    currentArchivalAid,
    setExpandedNodesIdsHEAP,
  } = etctx;

  const processAidEadChildren = useCallback(async () => {
    setCurrentArchivalUnit?.(undefined);
    if (currentArchivalAid?.id !== id) {
      setLoading?.(true);

      const aid = await singleFetch<ArchivalAidMetadata>(
        `${aidPrefix}/${id}`,
        `aid-detail-${id}`
      );

      if (!aid) {
        setLoading?.(false);
        clearContext();
        push('/not-found');
        return;
      }

      setCurrentArchivalAid?.(aid);

      const childrenIds = (aid.eadNode?.children ?? [])
        .filter((c) => c.childrenCount > 0)
        .map((c) => getNodeId(c)!);

      if (childrenIds.length > 0) {
        setNextIdsToExpand?.([...childrenIds]);
        setCurrentEadNode?.(aid.eadNode);
      }
      const path = createAidHierarchyPath(aid, variant!);
      setCrumbs?.(path);
      setLoading?.(false);
    } else {
      setCurrentEadNode?.(currentArchivalAid.eadNode);
      const path = createAidHierarchyPath(currentArchivalAid, variant!);
      setCrumbs?.(path);
      setNextIdsToExpand?.([
        ...(currentArchivalAid?.eadNode?.children ?? []).map(
          (c) => getNodeId(c)!
        ),
      ]);
    }
  }, [
    currentArchivalAid,
    id,
    setCrumbs,
    setCurrentArchivalAid,
    setCurrentArchivalUnit,
    setCurrentEadNode,
    setNextIdsToExpand,
    setLoading,
    variant,
    clearContext,
    push,
  ]);

  const processAidChildren = useCallback(async () => {
    setLoading?.(true);
    setExpandedNodesIdsHEAP?.({});
    setNextIdsToExpand?.([]);
    const aid = await singleFetch<ArchivalAidMetadata>(
      `${aidPrefix}/${id}`,
      `aid-detail-${id}`
    );
    if (!aid) {
      setLoading?.(false);
      clearContext();
      push('/not-found');
      return;
    }
    if (aid.hasEadNode || aid.eadNode) {
      setCurrentArchivalUnit?.(undefined);
      setLoading?.(false);
      processAidEadChildren();
      return;
    }

    const crumbs = createAidHierarchyPath(aid, variant!);
    setCrumbs?.(crumbs);
    setCurrentArchivalAid?.(aid);
    setCurrentArchivalUnit?.(undefined);
    setLoading?.(false);
  }, [
    setLoading,
    setExpandedNodesIdsHEAP,
    setNextIdsToExpand,
    id,
    variant,
    setCrumbs,
    setCurrentArchivalAid,
    setCurrentArchivalUnit,
    clearContext,
    push,
    processAidEadChildren,
  ]);

  const processArchivalUnit = useCallback(async () => {
    setLoading?.(true);
    const auCopy = await singleFetch<ArchivalUnitCopy>(
      `${archivalUnitApi}/${id}`,
      `archival-unit-detail-${id}`
    );
    if (!auCopy) {
      setLoading?.(false);
      clearContext();
      push('/not-found');
      return;
    }
    const crumbs = createArchivalUnitHierarchyPath(auCopy, variant!);
    setCrumbs?.(crumbs);
    setCurrentArchivalUnit?.(auCopy);
    setLoading?.(false);
  }, [
    id,
    setCrumbs,
    setCurrentArchivalUnit,
    setLoading,
    variant,
    push,
    clearContext,
  ]);

  return {
    processAidEadChildren,
    processArchivalUnit,
    processAidChildren,
  };
};

const useProcessEadChildren = (
  id: string,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const etctx = useEadTreeContext();
  const variant = useVariantContext();
  const clearContext = useClearContext();
  const { push } = useHistory();
  const {
    setCurrentArchivalAid,
    setCurrentEadNode,
    setCrumbs,
    setChildrenViewHeap,
    setExpandedNodesIdsHEAP,
    setNextIdsToExpand,
    currentArchivalAid,
    setCurrentArchivalUnit,
  } = etctx;

  const processEadChildren = useCallback(async () => {
    setLoading?.(true);
    setCurrentArchivalUnit?.(undefined);
    const node = await singleFetch<EadNode>(
      `${aidPrefix}/ead/${id}`,
      `ead-detail-${id}`
    );
    if (!node) {
      setLoading?.(false);
      clearContext();
      push('/not-found');
      return;
    }
    const path = createNestedEadHierarchyPath(node, variant!);
    const AID_LEVEL = variant === 'funds' ? 1 : 0;
    if (
      !currentArchivalAid ||
      currentArchivalAid.id !== path?.crumbs?.[AID_LEVEL].id
    ) {
      const currentAid = await singleFetch<ArchivalAidMetadata>(
        `${aidPrefix}/${path?.crumbs[AID_LEVEL].id}`,
        getCacheKey('aidDetail', path?.crumbs?.[AID_LEVEL].id ?? '')
      );

      setCurrentArchivalAid?.(currentAid);
      if (path?.crumbs?.[0] && variant === 'funds') {
        path.crumbs[0].label = currentAid.archivalCollection?.name ?? '';
      }
    } else {
      if (path?.crumbs?.[0] && variant === 'funds') {
        path.crumbs[0].label =
          currentArchivalAid.archivalCollection?.name ?? '';
      }
    }

    setCrumbs?.(path?.crumbs);
    const cheap = path?.cheap ?? ({} as Record<string, EadNode[]>);
    setChildrenViewHeap?.((p) => ({ ...p, ...cheap }));
    setExpandedNodesIdsHEAP?.((p) => {
      if (!path?.crumbs || path?.crumbs?.length === 0) {
        return p;
      } else {
        const newHeap: Record<string, boolean> = {};
        path?.crumbs.forEach((c) => (newHeap[c.id] = true));
        return { ...p, ...newHeap };
      }
    });

    setNextIdsToExpand?.((node?.children ?? []).map((c) => getNodeId(c)!));
    setCurrentEadNode?.(node);
    setLoading?.(false);
  }, [
    currentArchivalAid,
    id,
    setChildrenViewHeap,
    setCrumbs,
    setCurrentArchivalAid,
    setCurrentArchivalUnit,
    setCurrentEadNode,
    setExpandedNodesIdsHEAP,
    setNextIdsToExpand,
    setLoading,
    variant,
    clearContext,
    push,
  ]);
  return { processEadChildren };
};

export const useClearContext = () => {
  const {
    setCurrentArchivalAid,
    setCurrentArchivalCollection,
    setCurrentArchivalUnit,
    setCurrentEadNode,
    setExpandedNodesIdsHEAP,
    setNextIdsToExpand,
  } = useEadTreeContext();

  const clearContext = useCallback(() => {
    setCurrentArchivalAid?.(undefined);
    setCurrentArchivalCollection?.(undefined);
    setCurrentArchivalUnit?.(undefined);
    setCurrentEadNode?.(undefined);
    setExpandedNodesIdsHEAP?.({});
    setNextIdsToExpand?.([]);
  }, [
    setCurrentArchivalAid,
    setCurrentArchivalCollection,
    setCurrentArchivalUnit,
    setCurrentEadNode,
    setExpandedNodesIdsHEAP,
    setNextIdsToExpand,
  ]);
  return clearContext;
};

const useProcessParams = () => {
  const { ead, level, id } = useParams<{
    ead: string;
    level: string;
    id: string;
  }>();
  const isEad = ead === 'ead';
  const levelIndex = parseInt(level);
  const variant = useVariantContext();
  const [loading, setLoading] = useState(false);
  const { processCollectionDetail } = useProcessCollection(id, setLoading);
  const {
    processArchivalUnit,
    processAidEadChildren,
    processAidChildren,
  } = useProcessAidChildren(id, setLoading);
  const { processEadChildren } = useProcessEadChildren(id, setLoading);
  const clearContext = useClearContext();

  /*
  fondy
  	level     0 = fond
  	level     1 = pomucka
  	level n > 2 = ead / archival unit copy
  pomucky
  	level     0 = pomucka
  	level     1 = ead / archival unit copy
  	level n > 2 = ead
  */
  useEffect(() => {
    if (variant === 'funds') {
      if (levelIndex === 0) {
        clearContext();
        processCollectionDetail();
      } else if (levelIndex > 1 && isEad) {
        processEadChildren();
      } else if (levelIndex > 1 && !isEad) {
        processArchivalUnit();
      } else if (levelIndex === 1 && isEad) {
        processAidEadChildren();
      } else if (levelIndex === 1 && !isEad) {
        processAidChildren();
      } else {
        clearContext();
      }
    } else {
      if (levelIndex === 0 && !isEad) {
        processAidChildren();
      } else if (levelIndex === 0 && isEad) {
        processAidEadChildren();
      } else if (levelIndex === 1 && !isEad) {
        processArchivalUnit();
      } else if (levelIndex >= 1 && isEad) {
        processEadChildren();
      } else {
        clearContext();
      }
    }
  }, [
    isEad,
    levelIndex,
    processEadChildren,
    processAidEadChildren,
    processAidChildren,
    processCollectionDetail,
    processArchivalUnit,
    variant,
    clearContext,
  ]);

  return { localLoading: loading };
};
export default useProcessParams;
